import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutGrid, List } from 'lucide-react';
import { Button, Container, Empty, cn } from '@easymoving/ui';
import type { Destination, ProgressStatus } from '@easymoving/types';
import { useToast } from '@easymoving/ui';
import { useBackend } from '../lib/backend/context';
import { useBoxes, useItems } from '../hooks/useUserData';
import { ItemCard } from '../components/items/ItemCard';
import { ItemRow } from '../components/items/ItemRow';
import { BulkBar } from '../components/items/BulkBar';
import { RoomTabs } from '../components/items/RoomTabs';
import { ActiveFilterChips } from '../components/items/ActiveFilterChips';
import { AddItemTile, AddItemRow } from '../components/items/AddItemTile';
import type { Item } from '@easymoving/types';
import { applyItemQuery } from '../lib/query/itemFilters';
import {
  DEFAULT_QUERY,
  GalleryControls,
  type GalleryQuery,
} from '../components/items/GalleryControls';

type ViewMode = 'cards' | 'list';
const VIEW_KEY = 'easymoving.galleryView';

function readView(): ViewMode {
  return (typeof localStorage !== 'undefined' && localStorage.getItem(VIEW_KEY)) === 'list'
    ? 'list'
    : 'cards';
}

export function GalleryPage() {
  const { t } = useTranslation();
  const { items, loading } = useItems();
  const { boxes } = useBoxes();
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState<GalleryQuery>(DEFAULT_QUERY);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [view, setView] = useState<ViewMode>(readView);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const backend = useBackend();
  const toast = useToast();

  function changeView(next: ViewMode) {
    setView(next);
    if (next !== 'list') setSelected(new Set());
    try {
      localStorage.setItem(VIEW_KEY, next);
    } catch {
      /* ignore storage failures (private mode) */
    }
  }

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function bulkApply(patch: Partial<Item>) {
    const ids = [...selected];
    await Promise.all(ids.map((id) => backend.updateItem(id, patch)));
    toast.push({ title: t('bulk.applied', { count: ids.length }), tone: 'success' });
    setSelected(new Set());
  }

  async function bulkDelete() {
    const ids = [...selected];
    await Promise.all(
      ids.map((id) => {
        void backend.deleteItemImages(id);
        return backend.deleteItem(id);
      }),
    );
    toast.push({ title: t('bulk.deleted', { count: ids.length }), tone: 'success' });
    setSelected(new Set());
  }

  // Deep-link filters from the URL (e.g. dashboard cards → ?destination=sell).
  const paramKey = searchParams.toString();
  useEffect(() => {
    const destination = (searchParams.get('destination') as Destination | null) ?? undefined;
    const room = searchParams.get('room') ?? undefined;
    const progressStatus = (searchParams.get('status') as ProgressStatus | null) ?? undefined;
    const missingPhoto = searchParams.get('missing') === 'photo' || undefined;
    setQuery((q) => ({
      ...q,
      filters: { ...q.filters, destination, room, progressStatus, missingPhoto },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramKey]);

  // boxId → name, so search matches box names and cards can show "in box X".
  const boxNames = useMemo(() => new Map(boxes.map((b) => [b.id, b.name])), [boxes]);
  const visible = useMemo(
    () => applyItemQuery(items, { ...query, boxNames }),
    [items, query, boxNames],
  );

  if (loading) {
    return (
      <Container className="pt-6">
        <p className="text-graphite py-12">{t('common.loading')}</p>
      </Container>
    );
  }

  if (items.length === 0) {
    return (
      <Container className="pt-10">
        <Empty
          title={t('gallery.empty')}
          body={t('gallery.emptyBody')}
          action={
            <Button asChild size="lg">
              <Link to="/app/items/new">{t('gallery.addItem')}</Link>
            </Button>
          }
        />
      </Container>
    );
  }

  return (
    <Container className="pt-4">
      <div className="sticky top-16 z-20 bg-bg/90 backdrop-blur-md py-3 -mx-5 px-5 md:-mx-8 md:px-8">
        <GalleryControls
          query={query}
          onChange={setQuery}
          open={filtersOpen}
          onOpenChange={setFiltersOpen}
        />
      </div>

      <RoomTabs
        items={items}
        value={query.filters.room}
        onChange={(room) => setQuery((q) => ({ ...q, filters: { ...q.filters, room } }))}
      />
      <ActiveFilterChips
        filters={query.filters}
        search={query.search}
        onRemove={(key) => setQuery((q) => ({ ...q, filters: { ...q.filters, [key]: undefined } }))}
        onClearSearch={() => setQuery((q) => ({ ...q, search: '' }))}
      />

      <div className="flex items-center justify-between mb-3 mt-2">
        <h1 className="font-display text-xl tracking-snug">{t('gallery.title')}</h1>
        <div className="flex items-center gap-3">
          <span className="text-graphite-soft text-[14px] tnum">
            {t('gallery.resultCount', { count: visible.length })}
          </span>
          <div className="inline-flex rounded-md border border-rule overflow-hidden" role="group" aria-label={t('gallery.view')}>
            <ViewButton
              active={view === 'cards'}
              label={t('gallery.viewCards')}
              icon={LayoutGrid}
              onClick={() => changeView('cards')}
            />
            <ViewButton
              active={view === 'list'}
              label={t('gallery.viewList')}
              icon={List}
              onClick={() => changeView('list')}
            />
          </div>
        </div>
      </div>

      {visible.length === 0 ? (
        <Empty title={t('gallery.noResults')} body={t('gallery.noResultsBody')} />
      ) : view === 'list' ? (
        <ul
          className={cn('flex flex-col gap-2', selected.size > 0 && 'pb-20')}
          data-testid="item-grid"
        >
          {visible.map((item) => (
            <ItemRow
              key={item.id}
              item={item}
              selected={selected.has(item.id)}
              onToggleSelect={toggleSelect}
            />
          ))}
          {selected.size === 0 && <AddItemRow />}
        </ul>
      ) : (
        <div
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
          data-testid="item-grid"
        >
          {visible.map((item) => (
            <ItemCard key={item.id} item={item} boxName={item.boxId ? boxNames.get(item.boxId) : undefined} />
          ))}
          <AddItemTile />
        </div>
      )}

      {view === 'list' && selected.size > 0 && (
        <BulkBar
          count={selected.size}
          totalVisible={visible.length}
          onSelectAll={() => setSelected(new Set(visible.map((i) => i.id)))}
          onClear={() => setSelected(new Set())}
          onApply={bulkApply}
          onDelete={bulkDelete}
        />
      )}
    </Container>
  );
}

function ViewButton({
  active,
  label,
  icon: Icon,
  onClick,
}: {
  active: boolean;
  label: string;
  icon: typeof List;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      className={cn(
        'flex items-center justify-center size-8 transition-colors',
        active ? 'bg-petrol text-white' : 'bg-surface text-graphite hover:bg-surface-tint',
      )}
    >
      <Icon className="size-4" />
    </button>
  );
}
